'use client';

import React, { useState } from 'react';
import { ExtendedArchiMateDiagram, RuleSeverity } from '@/lib/archimate/extended-types';
import {
  validateDiagram,
  getViolationsBySeverity,
  calculateComplianceScore,
  generateValidationReport,
  BUILT_IN_RULES,
} from '@/lib/archimate/validation-utils';
import { X, Shield, AlertTriangle, CheckCircle2, AlertCircle, Info, Download } from 'lucide-react';
import { PanelHelpDialog, HelpIconButton } from './PanelHelpDialog';

interface ValidationPanelProps {
  diagram: ExtendedArchiMateDiagram;
  onUpdate: (diagram: ExtendedArchiMateDiagram) => void;
  onClose: () => void;
}

export default function ValidationPanel({ diagram, onUpdate, onClose }: ValidationPanelProps) {
  const [viewMode, setViewMode] = useState<'violations' | 'rules'>('violations');
  const [filterSeverity, setFilterSeverity] = useState<RuleSeverity | 'all'>('all');
  const [showHelp, setShowHelp] = useState(false);

  const violations = validateDiagram(diagram);
  const complianceScore = calculateComplianceScore(diagram);

  const critical = getViolationsBySeverity(violations, 'critical');
  const errors = getViolationsBySeverity(violations, 'error');
  const warnings = getViolationsBySeverity(violations, 'warning');
  const info = getViolationsBySeverity(violations, 'info');

  const filteredViolations =
    filterSeverity === 'all' ? violations : getViolationsBySeverity(violations, filterSeverity);

  const handleToggleRule = (ruleId: string) => {
    const updatedRules = diagram.rules.map((rule) =>
      rule.id === ruleId ? { ...rule, enabled: !rule.enabled } : rule
    );

    onUpdate({
      ...diagram,
      rules: updatedRules,
    });
  };

  const handleExportReport = () => {
    const report = generateValidationReport(diagram);
    const blob = new Blob([report], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${diagram.name}-validation-report.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getSeverityIcon = (severity: RuleSeverity) => {
    switch (severity) {
      case 'critical':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-accent" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      case 'info':
        return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  const getSeverityColor = (severity: RuleSeverity) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-500/10 border-red-500/30 text-red-400';
      case 'error':
        return 'bg-accent-muted border-border text-accent';
      case 'warning':
        return 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400';
      case 'info':
        return 'bg-blue-500/10 border-blue-500/30 text-blue-400';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    if (score >= 40) return 'text-accent';
    return 'text-red-500';
  };

  return (
    <div className="fixed right-0 top-0 h-screen w-[600px] bg-background border-l border-border flex flex-col shadow-2xl z-50 animate-in slide-in-from-right duration-300">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-accent" />
          <h2 className="text-xl font-bold text-foreground">Architecture Validation</h2>
          <HelpIconButton onClick={() => setShowHelp(true)} />
        </div>
        <button onClick={onClose} className="p-2 hover:bg-cream/10 rounded transition-colors">
          <X className="w-5 h-5 text-foreground" />
        </button>
      </div>

      {/* Help Dialog */}
      {showHelp && (
        <PanelHelpDialog
          panelKey="validation"
          onClose={() => setShowHelp(false)}
        />
      )}

      {/* Toolbar */}
      <div className="p-4 border-b border-border space-y-3">
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('violations')}
            className={`px-4 py-2 rounded transition-colors ${
              viewMode === 'violations' ? 'bg-accent text-accent-foreground' : 'bg-cream/10 text-foreground hover:bg-cream/20'
            }`}
          >
            Violations
          </button>
          <button
            onClick={() => setViewMode('rules')}
            className={`px-4 py-2 rounded transition-colors ${
              viewMode === 'rules' ? 'bg-accent text-accent-foreground' : 'bg-cream/10 text-foreground hover:bg-cream/20'
            }`}
          >
            Rules ({BUILT_IN_RULES.length})
          </button>
          <button
            onClick={handleExportReport}
            className="ml-auto px-4 py-2 bg-cream/10 text-foreground rounded hover:bg-cream/20 flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>

        {/* Filter by Severity (Violations view only) */}
        {viewMode === 'violations' && (
          <select
            value={filterSeverity}
            onChange={(e) => setFilterSeverity(e.target.value as RuleSeverity | 'all')}
            className="w-full px-4 py-2 bg-cream/10 border border-border rounded text-foreground focus:outline-none focus:border-accent"
          >
            <option value="all">All Severities ({violations.length})</option>
            <option value="critical">Critical ({critical.length})</option>
            <option value="error">Errors ({errors.length})</option>
            <option value="warning">Warnings ({warnings.length})</option>
            <option value="info">Info ({info.length})</option>
          </select>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Violations View */}
        {viewMode === 'violations' && (
          <div className="space-y-6">
            {/* Compliance Score */}
            <div className="p-6 bg-cream/5 border border-border rounded text-center">
              <div className="text-sm text-foreground-secondary mb-2">Compliance Score</div>
              <div className={`text-5xl font-bold ${getScoreColor(complianceScore)}`}>
                {complianceScore.toFixed(0)}<span className="text-2xl">/100</span>
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground-secondary">Critical</span>
                  <AlertTriangle className="w-4 h-4 text-red-500" />
                </div>
                <div className="text-2xl font-bold text-red-400 mt-1">{critical.length}</div>
              </div>
              <div className="p-3 bg-accent-muted border border-border rounded">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground-secondary">Errors</span>
                  <AlertCircle className="w-4 h-4 text-accent" />
                </div>
                <div className="text-2xl font-bold text-accent mt-1">{errors.length}</div>
              </div>
              <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground-secondary">Warnings</span>
                  <AlertTriangle className="w-4 h-4 text-yellow-500" />
                </div>
                <div className="text-2xl font-bold text-yellow-400 mt-1">{warnings.length}</div>
              </div>
              <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-foreground-secondary">Info</span>
                  <Info className="w-4 h-4 text-blue-500" />
                </div>
                <div className="text-2xl font-bold text-blue-400 mt-1">{info.length}</div>
              </div>
            </div>

            {/* Violations List */}
            <div>
              {filteredViolations.length === 0 ? (
                <div className="p-8 text-center text-green-500">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-3" />
                  <p className="font-bold mb-1">All Clear!</p>
                  <p className="text-sm text-foreground-secondary">No violations found for this filter</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredViolations.map((violation, index) => (
                    <div
                      key={index}
                      className={`p-4 border rounded ${getSeverityColor(violation.severity)}`}
                    >
                      <div className="flex items-start gap-3">
                        {getSeverityIcon(violation.severity)}
                        <div className="flex-1">
                          <div className="font-medium mb-1">{violation.ruleName}</div>
                          <div className="text-sm opacity-90 mb-2">{violation.message}</div>
                          {violation.suggestion && (
                            <div className="text-xs opacity-75 bg-black/20 rounded p-2">
                              💡 {violation.suggestion}
                            </div>
                          )}
                          {violation.elementIds.length > 0 && (
                            <div className="text-xs opacity-60 mt-2">
                              Affects {violation.elementIds.length} element(s)
                            </div>
                          )}
                        </div>
                        <span className="text-xs uppercase font-bold opacity-75">
                          {violation.severity}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Rules View */}
        {viewMode === 'rules' && (
          <div className="space-y-6">
            <div className="p-4 bg-cream/5 border border-border rounded">
              <h3 className="text-lg font-bold text-foreground mb-2">Built-in Validation Rules</h3>
              <p className="text-sm text-foreground-secondary">
                {BUILT_IN_RULES.filter((r) => r.enabled).length} of {BUILT_IN_RULES.length} rules enabled
              </p>
            </div>

            {/* Rules by Category */}
            {Object.entries(
              BUILT_IN_RULES.reduce((acc, rule) => {
                if (!acc[rule.category]) acc[rule.category] = [];
                acc[rule.category].push(rule);
                return acc;
              }, {} as Record<string, typeof BUILT_IN_RULES>)
            ).map(([category, rules]) => (
              <div key={category}>
                <h4 className="text-sm font-bold text-accent mb-3 uppercase tracking-wide">
                  {category}
                </h4>
                <div className="space-y-2">
                  {rules.map((rule) => {
                    const ruleViolations = violations.filter((v) => v.ruleId === rule.id);
                    return (
                      <div
                        key={rule.id}
                        className="p-4 bg-cream/5 border border-border rounded hover:bg-cream/10 transition-colors"
                      >
                        <div className="flex items-start gap-3">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={rule.enabled}
                              onChange={() => handleToggleRule(rule.id)}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-cream/20 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent"></div>
                          </label>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-foreground">{rule.name}</span>
                              {ruleViolations.length > 0 && (
                                <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded text-xs font-bold">
                                  {ruleViolations.length}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-foreground-secondary">{rule.description}</p>
                            <div className="flex items-center gap-2 mt-2">
                              {getSeverityIcon(rule.severity)}
                              <span className="text-xs text-foreground-tertiary capitalize">{rule.severity}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Custom Rules Section */}
            <div className="p-4 bg-cream/5 border-2 border-dashed border-border rounded text-center">
              <p className="text-sm text-foreground-secondary mb-2">Want to add custom validation rules?</p>
              <button className="px-4 py-2 bg-accent text-accent-foreground rounded hover:bg-accent-hover text-sm font-medium">
                Create Custom Rule
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
